import { Component, inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedPackage } from 'src/app/services/bdoservice/users/models/activated-package.model';
import { TranslateService } from '@ngx-translate/core';
import { ColumnRow, TableConfig } from '@ekowitryna/ui';
import { Subject, takeUntil } from 'rxjs';
import { TableLazyLoadEvent } from 'primeng/table';

@Component({
  selector: 'app-profile-packages-list',
  templateUrl: './packages-profile-list.component.html',
  standalone: false

})
export class PackagesProfileListComponent implements OnInit, OnDestroy {

  @Input()
  packages: ActivatedPackage[] = [];

  @ViewChild('statusTemplate', { static: true }) statusTemplateRef!: TemplateRef<unknown>;

  private destroy$ = new Subject<void>();

  columnRows: ColumnRow[] = [];

  tableConfig: TableConfig = {
    paginator: true,
    rows: 10,
    loading: false,
    totalRecords: 0,
    filterable: false,
    editable: false,
    actionsPosition: 'right',
    first: 0,
    responsiveLayout: 'scroll',
    editMode: 'row',
    tableMenuItems: [],
    dataKey: 'name',
    reorderableColumns: false,
    resizableColumns: true,
    stateKey: 'table-packages-profile-v1'
  };

  private readonly translate: TranslateService = inject(TranslateService);

  ngOnInit(): void {
    this.initTableColumns();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initTableColumns();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initTableColumns(): void {
    this.columnRows = [
      {
        columns: [
          {
            field: 'name',
            type: 'text',
            load: (pkg: ActivatedPackage) => pkg.name,
            header: this.translate.instant('profile.packages.name'),
            resizable: true,
            sortable: {
              enabled: true,
              field: 'name'
            }
          },
          {
            field: 'operationCount',
            type: 'text',
            load: (pkg: ActivatedPackage) => this.getOperationCountText(pkg),
            header: this.translate.instant('profile.packages.operations'),
            resizable: true,
            sortable: {
              enabled: true,
              field: 'operationCount'
            }
          },
          {
            field: 'createDate',
            type: 'text',
            load: (pkg: ActivatedPackage) => this.formatDate(pkg.createDate),
            header: this.translate.instant('profile.packages.purchaseDate'),
            resizable: true,
            sortable: {
              enabled: true,
              field: 'createDate'
            }
          },
          {
            field: 'activated',
            type: 'custom',
            customTypeTemplate: this.statusTemplateRef,
            load: (pkg: ActivatedPackage) => pkg,
            header: this.translate.instant('profile.packages.status'),
            resizable: true,
            sortable: {
              enabled: true,
              field: 'activated'
            }
          },
          {
            field: 'endDate',
            type: 'text',
            load: (pkg: ActivatedPackage) => this.formatDate(pkg.endDate),
            header: this.translate.instant('profile.packages.endDate'),
            resizable: true,
            sortable: {
              enabled: true,
              field: 'endDate'
            }
          }
        ]
      }
    ];
  }

  dataProvider = async (event: TableLazyLoadEvent) => {
    let sortedPackages = [...this.packages];

    // Sortowanie
    if (event.sortField && typeof event.sortField === 'string') {
      const sortField = event.sortField as keyof ActivatedPackage;
      sortedPackages.sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];
        const sortOrder = event.sortOrder === 1 ? 1 : -1;

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (aValue < bValue) return -1 * sortOrder;
        if (aValue > bValue) return 1 * sortOrder;
        return 0;
      });
    } else {
      // Domyślne sortowanie: aktywne najpierw, potem według daty utworzenia
      sortedPackages.sort((a, b) => {
        if (a.activated && !b.activated) return -1;
        if (!a.activated && b.activated) return 1;
        return new Date(b.createDate).getTime() - new Date(a.createDate).getTime();
      });
    }

    // Paginacja
    const start = event.first || 0;
    const end = start + (event.rows || 10);
    const paginatedData = sortedPackages.slice(start, end);

    return {
      data: paginatedData,
      totalRecords: sortedPackages.length
    };
  };

  getStatusText(_package: ActivatedPackage): string {
    if (_package.activated) {
      return this.translate.instant('profile.packages.statusActive');
    } else {
      return this.translate.instant('profile.packages.statusInactive');
    }
  }

  getOperationCountText(_package: ActivatedPackage): string {
    if (_package.operationCount == null) {
      return this.translate.instant('profile.packages.unlimited');
    } else {
      return _package.operationCount.toString();
    }
  }

  formatDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  getStatusSeverity(_package: ActivatedPackage): 'success' | 'danger' {
    return _package.activated ? 'success' : 'danger';
  }
}
